import { GRACE_PERIOD } from './use-network-grace-period';
import { isOfflineModeAllowed } from '@/utils/offline-mode';
import {
  NetworkStatus,
  useNetworkStatus,
} from '@/contexts/network-status-context';
import { OfflineModeSimulator } from '@/utils/offline-mode/offline-mode-simulator';
import { useEffect, useState } from 'react';

/**
 * Hook that debounes the network status to return an updated isOfflineModeAllowed
 * and re-render a component only when we're "certain" the network status is stable.
 *
 * This should be what we use most of the time to determine if offline mode is active,
 * but may return false on the component's first render (even if offline). If the component
 * needs an immediate deterministic answer, use getOfflineModeActive.
 */
export const useOfflineModeActive = () => {
  const { networkStatus } = useNetworkStatus();
  const [localNetworkStatus, setLocalNetworkStatus] = useState(networkStatus);
  const isOfflineModeSimulated =
    OfflineModeSimulator.getInstance().checkEnabled();

  useEffect(() => {
    const handler = setTimeout(() => {
      setLocalNetworkStatus(networkStatus);
    }, GRACE_PERIOD);

    // Clear the timeout if networkStatus changes before the delay is over
    return () => clearTimeout(handler);
  }, [networkStatus]);

  return (
    isOfflineModeSimulated ||
    (isOfflineModeAllowed && localNetworkStatus === NetworkStatus.DISCONNECTED)
  );
};

export const useOfflineModeActiveWithoutDelay = () => {
  const { networkStatus } = useNetworkStatus();
  const isOfflineModeSimulated =
    OfflineModeSimulator.getInstance().checkEnabled();

  return (
    isOfflineModeSimulated ||
    (isOfflineModeAllowed && networkStatus === NetworkStatus.DISCONNECTED)
  );
};
